import { DataGrid, DataGridColumn } from '../../design-system';
import { AnaloguesCount, RockId } from '../../@types/gheInputs/types';

export type TargetRockRow = {
  rock_id: RockId;
  porosity?: number | null;
  permeability_md?: number | null;
  analogues_count?: AnaloguesCount | '';
};

type Props = {
  rows: TargetRockRow[];
  onChange: (nextRows: TargetRockRow[]) => void;
  loading?: boolean;
};

const sanitizeNumeric = (value: string) => value.replace(/[^0-9.,]/g, '');

const parseNumber = (raw: string): number | null => {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  const asNumber = Number(trimmed);
  return Number.isNaN(asNumber) ? null : asNumber;
};

const parseAnalogues = (raw: string): AnaloguesCount | '' => {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (trimmed.toLowerCase() === 'auto') return 'auto';
  const asNumber = Number(trimmed.replace(',', '.'));
  return Number.isNaN(asNumber) || !Number.isFinite(asNumber) ? '' : asNumber;
};

export const GheTargetRocksForm = ({ rows, onChange, loading }: Props) => {
  const handleRowSave = (updated: TargetRockRow, index: number) => {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      return {
        rock_id: row.rock_id,
        porosity: parseNumber(String(updated.porosity ?? '')),
        permeability_md: parseNumber(String(updated.permeability_md ?? '')),
        analogues_count: parseAnalogues(String(updated.analogues_count ?? ''))
      };
    });
    onChange(next);
  };

  const handleFill = (updates: { index: number; row: TargetRockRow }[]) => {
    const next = [...rows];
    updates.forEach(({ index, row }) => {
      next[index] = row;
    });
    onChange(next);
  };

  const columns: DataGridColumn<TargetRockRow>[] = [
    {
      key: 'rock_id',
      header: 'Rock',
      width: '120px',
      editable: false,
      getValue: row => row.rock_id
    },
    {
      key: 'porosity',
      header: 'Porosity (frac.)',
      align: 'right',
      editable: true,
      sanitizeInput: sanitizeNumeric,
      getValue: row => row.porosity ?? ''
    },
    {
      key: 'permeability_md',
      header: 'Permeability (mD)',
      align: 'right',
      editable: true,
      sanitizeInput: sanitizeNumeric,
      getValue: row => row.permeability_md ?? ''
    },
    {
      key: 'analogues_count',
      header: 'Analogues Count',
      align: 'right',
      editable: true,
      getValue: row => row.analogues_count ?? 'auto'
    }
  ];

  return (
    <DataGrid<TargetRockRow>
      columns={columns}
      data={rows}
      rowKey="rock_id"
      editableRows
      striped
      onRowSave={handleRowSave}
      onFill={handleFill}
      loading={loading}
      loadingRows={rows.length || 10}
      emptyMessage="No target rocks."
    />
  );
};
